import React from 'react';
import { Badge } from './ui/badge';
import { Zap } from 'lucide-react';

const MANUAL_LABELS = {
  not_raised: { label: 'DNO Not Raised', style: 'bg-slate-50 text-slate-600 border-slate-200' },
  submitted: { label: 'DNO Submitted', style: 'bg-amber-100 text-amber-800 border-amber-300' },
  approved: { label: 'DNO Approved', style: 'bg-blue-100 text-blue-800 border-blue-300' },
  commissioned: { label: 'DNO Commissioned', style: 'bg-emerald-100 text-emerald-800 border-emerald-300' },
  refused: { label: 'DNO Refused', style: 'bg-rose-100 text-rose-800 border-rose-300' },
  exempt: { label: 'DNO Not Required', style: 'bg-slate-100 text-slate-700 border-slate-300' },
};

/**
 * DNO progression badge.
 *
 * Source-of-truth precedence (per admin choice 2026-05-15):
 *   1. Live ENA Connect Direct fields (`dno_application_submitted_date`,
 *      `dno_application_confirmed_date`, `dno_commissioning_confirmed_date`,
 *      `dno_reference`) → always win when present.
 *   2. Manual override (`dno_manual_status`) → only shown when no live ENA
 *      record is linked.
 *
 * Props:
 *   customer       — required
 *   className      — passed to Badge
 *   showWhenAbsent — show "DNO Not Raised" pill when nothing exists; used on
 *                    Customer Detail header. Defaults to false (table/Kanban
 *                    where empty = render nothing to reduce noise).
 */
export const DnoStatusBadge = ({ customer, className = '', showWhenAbsent = false }) => {
  const submitted = customer?.dno_application_submitted_date;
  const confirmed = customer?.dno_application_confirmed_date;
  const commissioned = customer?.dno_commissioning_confirmed_date;
  const ref = customer?.dno_reference;
  const manual = customer?.dno_manual_status;
  const manualNote = customer?.dno_manual_status_note;
  const exportLimited = customer?.dno_export_limited;
  const exportNote = customer?.dno_export_limit_note;

  const exportPill = exportLimited ? (
    <Badge
      className="bg-orange-100 text-orange-800 border-orange-300 border text-[10px] flex items-center gap-0.5"
      title={exportNote ? `Export limitation: ${exportNote}` : 'Export limitation applies'}
      data-testid={`dno-export-limit-${customer?.id || 'x'}`}
    >
      <Zap className="w-2.5 h-2.5" />
      {exportNote ? `Export: ${exportNote}` : 'Export Limited'}
    </Badge>
  ) : null;

  const hasLive = submitted || confirmed || commissioned || ref;

  // 1. Live ENA wins
  if (hasLive) {
    let style = 'bg-slate-100 text-slate-700 border-slate-200';
    let label = 'DNO Filed';
    let title = ref ? `DNO ref: ${ref}` : 'DNO reference recorded';
    if (commissioned) {
      style = 'bg-emerald-100 text-emerald-800 border-emerald-300';
      label = 'DNO Commissioned';
      title = `DNO commissioned ${commissioned}`;
    } else if (confirmed) {
      style = 'bg-blue-100 text-blue-800 border-blue-300';
      label = 'DNO Approved';
      title = `DNO approved ${confirmed}`;
    } else if (submitted) {
      style = 'bg-amber-100 text-amber-800 border-amber-300';
      try {
        const days = Math.floor((Date.now() - new Date(submitted).getTime()) / 86400000);
        label = days >= 0 ? `DNO Submitted ${days}d` : 'DNO Submitted';
        title = `DNO submitted ${submitted} (${days}d ago)${ref ? ` · ref ${ref}` : ''}`;
      } catch {
        label = 'DNO Submitted';
      }
    }
    return (
      <span className="inline-flex items-center gap-1">
      <Badge
        className={`${style} border text-[10px] flex items-center gap-0.5 ${className}`}
        title={title}
        data-testid={`dno-status-${customer?.id || 'x'}`}
      >
        <Zap className="w-2.5 h-2.5" />
        {label}
      </Badge>
      {exportPill}
      </span>
    );
  }

  // 2. Manual override (only shown when no live record)
  if (manual && MANUAL_LABELS[manual]) {
    const m = MANUAL_LABELS[manual];
    return (
      <span className="inline-flex items-center gap-1">
      <Badge
        className={`${m.style} border text-[10px] flex items-center gap-0.5 ${className}`}
        title={manualNote ? `${m.label} — ${manualNote} (manual)` : `${m.label} (manual)`}
        data-testid={`dno-status-${customer?.id || 'x'}`}
      >
        <Zap className="w-2.5 h-2.5" />
        {m.label}
      </Badge>
      {exportPill}
      </span>
    );
  }

  // 3. Nothing recorded
  if (showWhenAbsent) {
    return (
      <span className="inline-flex items-center gap-1">
      <Badge
        className={`${MANUAL_LABELS.not_raised.style} border text-[10px] flex items-center gap-0.5 ${className}`}
        title="No DNO application has been raised for this customer"
        data-testid={`dno-status-${customer?.id || 'x'}`}
      >
        <Zap className="w-2.5 h-2.5" />
        DNO Not Raised
      </Badge>
      {exportPill}
      </span>
    );
  }
  return exportPill;
};

export const DNO_MANUAL_STATUS_OPTIONS = MANUAL_LABELS;

export default DnoStatusBadge;
